import { Component, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ChecklistService } from '../../checklist.service';
import { AppTextService } from '@fi-shared/app-text';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { isDefined } from '@fi-shared/utils/is-defined';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { YesNoPipe } from '@fi-app/utilities/fi-pipes/yes-no.pipe';
import { MatRadioGroup } from '@angular/material/radio';
import { ControlContainer, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { FiPageCollapsableContentComponent } from '@fi-app/components/fi-page-collapsable-content/fi-page-collapsable-content.component';
import { ChecklistModel } from '../../checklist.model';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { Subject } from 'rxjs';
import { fiDebounce } from '@fi-shared/utils';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { getLastItem } from '@fi-shared/utils/get-last-Item';

@Component({
    selector: 'fi-checklist-edit-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklistEditSettingsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklistEditSettingsComponent,
        },
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: NgForm,
        },
    ],
    host: { class: 'fi-checklist-edit-settings' },
    standalone: false,
})
export class FiChecklistEditSettingsComponent
    extends ControlValueAccessorValidatorBase<ChecklistModel>
    implements OnInit
{
    @Input() checklist: ChecklistModel;
    @Input() templateChanged$: Subject<void> = new Subject<void>();

    publicationMinStartDate = undefined;
    timeSettings;
    repeatFrequencyDropdownSettings = new DropdownSettingsModelV2<INameIdObj>({ orderBy: 'Id' });

    monthsDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    daysDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    hoursDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();

    dueTimeMonthsDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    dueTimeDaysDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    dueTimeHoursDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();

    remindTimeMonthsDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    remindTimeDaysDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();
    remindTimeHoursDropdownSettings = new DropdownSettingsModelV2<INameIdObj>();

    AvailableMonths = [];
    AvailableDays = [];
    ChecklistLocal = ApplicationSettingsType.ChecklistLocal;
    AccessRightType = AccessRightType;
    isAdmin;
    remindTimeMaxDate;

    @ViewChild('collapsableContent', { static: false }) collapsableContent: FiPageCollapsableContentComponent;

    constructor(
        private injector: Injector,
        public checklistService: ChecklistService,
        public appTextService: AppTextService,
        public accessToApplicationType: AccessToApplicationTypeService,
        private yesNoPipe: YesNoPipe,
        private localeService: FiLocaleService,
        private customerDataService: FiCustomerDataService,
    ) {
        super(injector);
        this.timeSettings = this.checklistService.timeSettings;
    }

    ngOnInit() {
        this.checklist = this.ngModel;
        this.setRepeatFrequencyDataSet();
        this.setHoursDataSet();

        this.isAdmin = this.accessToApplicationType.HasAccessToApplicationType(
            ApplicationSettingsType.ChecklistLocal,
            AccessRightType.Admin,
        );

        this.publicationSettingOnChange();

        this.publicationMinStartDate = this.checklist.CreatedOn || new Date().toString();
        this._rebuildData();

        this.templateChanged$.subscribe(() => this._rebuildData());
    }

    private setRepeatFrequencyDataSet() {
        this.repeatFrequencyDropdownSettings.setDataSet([
            {
                Id: ChecklistModel.RepeatFrequency.Monthly,
                Name: this.appTextService.get('checklist.repeat_frequency_monthly'),
            },
            {
                Id: ChecklistModel.RepeatFrequency.Daily,
                Name: this.appTextService.get('checklist.repeat_frequency_daily'),
            },
        ]);
    }

    private setHoursDataSet() {
        const hours: INameIdObj[] = [];
        for (let hour = 0; hour < 24; hour++) {
            hours.push({
                Id: hour,
                Name: hour.toString().paddingLeft('00') + ':00',
            });
        }
        this.hoursDropdownSettings.setDataSet(hours);
    }

    _rebuildData() {
        this.updateRecurringDays();
        this.updateDueTime();
        this.updateRemindMonths();
        this.updateRemindDays();
        this.updateRemindHours();
    }

    isRecurring(checklist) {
        return checklist.TimeSetting === ChecklistModel.TimeSettings.Recurring;
    }

    isOneTime(checklist) {
        return checklist.TimeSetting === ChecklistModel.TimeSettings.OneTime;
    }

    isTimeLimited() {
        return this.checklist.PublicationSetting.PublicationType === PublicationType.TimeLimited;
    }

    doPropagateChangesDebounced = fiDebounce(() => {
        super.doPropagateChanges();
    });
    doPropagateChanges() {
        this.doPropagateChangesDebounced();
    }

    publicationSettingOnChange() {
        this.checklist.RemindDateTime = this.checklist.PublicationSetting.PublicationStartDate
            ? new Date(this.checklist.PublicationSetting.PublicationStartDate)
            : null;
        if (this.checklist.PublicationSetting.PublicationEndDate) {
            this.remindTimeMaxDate = new Date(this.checklist.PublicationSetting.PublicationEndDate);
            this.remindTimeMaxDate.setDate(this.remindTimeMaxDate.getDate() - 1);
        } else {
            this.remindTimeMaxDate = undefined;
        }
        this.updateAvailableMonths();
        this.updateAvailableDays();
    }

    updateAvailableMonths() {
        this.AvailableMonths.length = 0;
        const publicationSetting = this.checklist.PublicationSetting;
        let i;
        if (
            this.isTimeLimited() &&
            publicationSetting.PublicationEndDate &&
            publicationSetting.PublicationStartDate.getFullYear() ===
                publicationSetting.PublicationEndDate.getFullYear()
        ) {
            for (
                i = publicationSetting.PublicationStartDate.getMonth();
                i <= publicationSetting.PublicationEndDate.getMonth();
                i++
            ) {
                this.AvailableMonths.push(i);
            }
        } else if (
            this.isTimeLimited() &&
            publicationSetting.PublicationEndDate &&
            publicationSetting.PublicationStartDate.getFullYear() + 1 ===
                publicationSetting.PublicationEndDate.getFullYear() &&
            publicationSetting.PublicationEndDate.getMonth() < publicationSetting.PublicationStartDate.getMonth()
        ) {
            for (i = publicationSetting.PublicationStartDate.getMonth(); i < 12; i++) {
                this.AvailableMonths.push(i);
            }
            for (i = 0; i <= publicationSetting.PublicationEndDate.getMonth(); i++) {
                this.AvailableMonths.push(i);
            }
        } else {
            this.AvailableMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
        }

        this.updateMonthsDropdownDataSet();
    }

    updateMonthsDropdownDataSet() {
        this.monthsDropdownSettings.setDataSet(
            this.AvailableMonths.map((i) => {
                return {
                    Id: i + 1,
                    Name: this.localeService.getMonthName(i),
                };
            }),
        );

        // this.MonthsDropdownSettings.DataSet.length = 0;
        // this.AvailableMonths.forEach((i) => {
        //     this.MonthsDropdownSettings.DataSet.push({
        //         Id: i + 1,
        //         Name: this.localeService.getMonthName(i)
        //     });
        // });
    }

    resetRecurringMonths() {
        this.checklist.RecurringMonths = [];
        if (this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Daily) {
            this.monthsDropdownSettings.dataSet.forEach((item: INameIdObj) => {
                this.checklist.RecurringMonths.push(item.Id);
            });
        }
        this.doPropagateChanges();
    }

    resetRecurringDays() {
        this.checklist.RecurringDays = [];
        this.doPropagateChanges();
    }

    updateAvailableDays() {
        this.AvailableDays.length = 0;
        if (this.isMonthly()) {
            this.updateRecurringDaysMonthly();
        } else if (this.isDaily()) {
            this.updateRecurringDaysDaily();
        }
    }

    addWeekDay(dataSet: any[], n) {
        dataSet.push({
            Id: n,
            Name: this.localeService.getDayName(n),
        });
    }

    updateRecurringDaysDaily() {
        const dataSet = [];
        const publicationSetting = this.checklist.PublicationSetting;
        let i;
        if (
            this.isTimeLimited() &&
            publicationSetting.PublicationStartDate &&
            publicationSetting.PublicationEndDate &&
            (<any>publicationSetting.PublicationEndDate - <any>publicationSetting.PublicationStartDate) /
                1000 /
                60 /
                60 /
                24 <
                7
        ) {
            const startDay = publicationSetting.PublicationStartDate.getDay(),
                endDay = publicationSetting.PublicationEndDate.getDay();
            if (startDay <= endDay) {
                for (i = startDay; i <= endDay; i++) {
                    this.addWeekDay(dataSet, i);
                }
            } else {
                for (i = startDay; i < 7; i++) {
                    this.addWeekDay(dataSet, i);
                }
                for (i = 0; i <= endDay; i++) {
                    this.addWeekDay(dataSet, i);
                }
            }
        } else {
            const daysCount = this.localeService.getDaysCount();
            for (let day = 0; day < daysCount; day++) {
                i = day + this.localeService.startingDay;
                if (i >= daysCount) {
                    i -= daysCount;
                }
                this.addWeekDay(dataSet, i);
            }
        }

        this.daysDropdownSettings.setDataSet(dataSet);
    }

    private updateRecurringDaysMonthly() {
        const dataSet = [];
        const sdt: Date = this.checklist.PublicationSetting.PublicationStartDate;
        const edt: Date = this.checklist.PublicationSetting.PublicationEndDate;
        const startMonth = sdt.getMonth();
        const endMonth: number = edt && edt.getMonth ? edt.getMonth() : undefined;
        let i: number;

        if (this.isTimeLimited() && sdt.getFullYear() === edt.getFullYear() && startMonth === endMonth) {
            for (i = sdt.getDate(); i <= edt.getDate(); i++) {
                dataSet.push({
                    Id: i,
                    Name: i,
                });
            }
        } else if (
            this.isTimeLimited() &&
            sdt.getFullYear() === edt.getFullYear() &&
            startMonth + 1 === endMonth &&
            sdt.getDate() > edt.getDate()
        ) {
            this.checklist.RecurringMonths.forEach((month) => {
                if (month - 1 === endMonth) {
                    for (i = 1; i <= edt.getDate(); i++) {
                        dataSet.push({
                            Id: i,
                            Name: i,
                        });
                    }
                } else if (month - 1 === startMonth) {
                    for (i = sdt.getDate(); i <= getDaysInMonth(month); i++) {
                        dataSet.push({
                            Id: i,
                            Name: i,
                        });
                    }
                }
            });
        } else {
            let max = 31;

            this.checklist.RecurringMonths.forEach((month) => {
                const daysInMonth = getDaysInMonth(month);
                if (daysInMonth < max) {
                    max = daysInMonth;
                }
            });

            for (let day = 1; day <= max; day++) {
                dataSet.push({
                    Id: day,
                    Name: day,
                });
            }
        }

        this.daysDropdownSettings.setDataSet(dataSet);
    }

    updateRecurringDays() {
        if (!this.checklist.RepeatFrequency) {
            this.daysDropdownSettings.setDataSet([]);
            return;
        }
        switch (this.checklist.RepeatFrequency) {
            case ChecklistModel.RepeatFrequency.Daily:
                this.updateRecurringDaysDaily();
                break;
            case ChecklistModel.RepeatFrequency.Monthly:
                this.updateRecurringDaysMonthly();
                break;
        }
    }

    resetRecurringHours() {
        this.checklist.RecurringHours = [];
        if (this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly) {
            this.checklist.RecurringHours.push(0);
        }
        this.doPropagateChanges();
    }

    resetDueTime() {
        this.checklist.DueTime.Months = undefined;
        this.checklist.DueTime.Days = undefined;
        this.checklist.DueTime.Hours = undefined;

        this.resetRemindTime();
        this.doPropagateChanges();
    }

    resetRemindMonths() {
        this.checklist.RemindTime.Months = undefined;
        this.doPropagateChanges();
    }

    resetRemindDays() {
        this.checklist.RemindTime.Days = undefined;
        this.doPropagateChanges();
    }

    resetRemindHours() {
        this.checklist.RemindTime.Hours = undefined;
        this.doPropagateChanges();
    }

    resetRemindTime() {
        this.resetRemindMonths();
        this.resetRemindDays();
        this.resetRemindHours();
        this.doPropagateChanges();
    }

    getMonthsText(n) {
        return this.appTextService.get(n === 1 ? 'checklist.n_month' : 'checklist.n_months').replace('{n}', n);
    }

    getDaysText(n) {
        return this.appTextService.get(n === 1 ? 'checklist.n_day' : 'checklist.n_days').replace('{n}', n);
    }

    getHoursText(n) {
        return this.appTextService.get(n === 1 ? 'checklist.n_hour' : 'checklist.n_hours').replace('{n}', n);
    }

    buildDueTimeDropdowns(
        srcData,
        srcDropdownSettings: DropdownSettingsModelV2<INameIdObj>,
        dueTimeDropdownSettings: DropdownSettingsModelV2<INameIdObj>,
        onlyZeroValue: boolean,
        skipZeroValue: boolean,
        getNameFn: (id: number) => string,
    ) {
        const data = [];
        if (!srcDropdownSettings.dataSet.length) {
            // don't do anything
        } else if (onlyZeroValue) {
            data.push({ Id: 0, Name: getNameFn(0) });
        } else {
            if (!skipZeroValue) {
                data.push({ Id: 0, Name: getNameFn(0) });
            }
            const sortedData = srcData.slice().sort(numbersSort),
                sortedDataSet = srcDropdownSettings.dataSet.map((item) => item.Id).sort(numbersSort);
            let maxValue;

            maxValue = getLastItem(sortedDataSet);
            if (sortedDataSet[0] === 0) {
                maxValue++;
            }

            // add one more day to calculate properly next day after the last one
            sortedData.push(sortedData[0] + sortedDataSet.length);

            for (let i = 1; i < sortedData.length; i++) {
                const diff = sortedData[i] - sortedData[i - 1];
                if (diff < maxValue) {
                    maxValue = diff;
                }
            }

            for (let ri = 1; ri <= maxValue; ri++) {
                data.push({ Id: ri, Name: getNameFn(ri) });
            }
        }

        dueTimeDropdownSettings.setDataSet(data);
    }

    updateDueTime(opts?: { skipUpdateMonths?: boolean; skipUpdateDays?: boolean }) {
        opts = opts || {};

        if (!opts.skipUpdateMonths) {
            // if we choose 2 day values we can choose only 0 months
            const onlyZeroValue = this.checklist.RecurringDays.length > 1;
            this.buildDueTimeDropdowns(
                this.checklist.RecurringMonths,
                this.monthsDropdownSettings,
                this.dueTimeMonthsDropdownSettings,
                onlyZeroValue,
                false,
                (id) => this.getMonthsText(id),
            );
        }

        if (!opts.skipUpdateDays) {
            // value 0 months 0 days should not be available
            let skipZeroValue = this.checklist.DueTime.Months === 0;

            // if we choose 2 hour values we can choose only 0 days
            // if we choose max from available months we can choose only 0 days
            let dueMonthsValues = this.dueTimeMonthsDropdownSettings.dataSet.sort(propComparator('Id')),
                dueMonthsMaxValue = dueMonthsValues.length ? dueMonthsValues[dueMonthsValues.length - 1].Id : undefined;
            const onlyZeroValue =
                this.checklist.RecurringHours.length > 1 ||
                (this.checklist.DueTime.Months != 0 && this.checklist.DueTime.Months === dueMonthsMaxValue);

            this.buildDueTimeDropdowns(
                this.checklist.RecurringDays,
                this.daysDropdownSettings,
                this.dueTimeDaysDropdownSettings,
                onlyZeroValue,
                skipZeroValue,
                (id) => this.getDaysText(id),
            );
        }

        // value 0 days 0 hours should not be available
        let skipZeroValue = this.checklist.DueTime.Days === 0;

        // if we choose max from available days we can choose only 0 hours
        let dueDaysValues = this.dueTimeDaysDropdownSettings.dataSet.sort(propComparator('Id')),
            dueDaysMaxValue = dueDaysValues.length ? dueDaysValues[dueDaysValues.length - 1].Id : undefined;
        const onlyZeroValue = this.checklist.DueTime.Days != 0 && this.checklist.DueTime.Days === dueDaysMaxValue;

        this.buildDueTimeDropdowns(
            this.checklist.RecurringHours,
            this.hoursDropdownSettings,
            this.dueTimeHoursDropdownSettings,
            onlyZeroValue,
            skipZeroValue,
            (id) => this.getHoursText(id),
        );
    }

    updateRemindTimeDropdown(
        remindTimeDropdownSettings: DropdownSettingsModelV2<{ Id: number; Name: string }>,
        maxValue: number,
        getNameFn: (id: number) => string,
    ) {
        const data = [];
        if (isDefined(maxValue)) {
            for (let id = 0; id <= maxValue; id++) {
                data.push({
                    Id: id,
                    Name: getNameFn(id),
                });
            }
        }
        remindTimeDropdownSettings.setDataSet(data);
    }

    updateRemindMonths() {
        let maxMonth = this.checklist.DueTime.Months;
        this.updateRemindTimeDropdown(this.remindTimeMonthsDropdownSettings, maxMonth, (id) => this.getMonthsText(id));
    }

    updateRemindDays() {
        if (this.isMonthly()) {
            this.updateRemindDaysMonthly();
        } else if (this.isDaily()) {
            this.updateRemindDaysDaily();
        }

        this.remindTimeDaysDropdownSettings.dataSet.forEach((item) => {
            item.Name = this.getDaysText(item.Id);
        });

        this.remindTimeDaysDropdownSettings.setDataSet(this.remindTimeDaysDropdownSettings.dataSet.slice());
    }

    updateRemindDaysMonthly() {
        if (!isDefined(this.checklist.RemindTime.Months) && this.checklist.RecurringDays.length === 1) return;

        let maxDays: number;
        if (this.checklist.RemindTime.Months === this.checklist.DueTime.Months) {
            maxDays = this.checklist.DueTime.Days;
        } else if (this.checklist.RemindTime.Months < this.checklist.DueTime.Months) {
            const dueDaysValues = this.dueTimeDaysDropdownSettings.dataSet.sort(propComparator('Id'));
            maxDays = dueDaysValues[dueDaysValues.length - 1].Id;
        }

        this.updateRemindTimeDropdown(this.remindTimeDaysDropdownSettings, maxDays, (id) => this.getDaysText(id));
    }

    updateRemindDaysDaily() {
        const maxDays = this.checklist.DueTime.Days;
        this.updateRemindTimeDropdown(this.remindTimeDaysDropdownSettings, maxDays, (id) => this.getDaysText(id));
    }

    updateRemindHours() {
        this.remindTimeHoursDropdownSettings.dataSet.length = 0;

        if (this.isMonthly()) return;

        let maxValue: number;

        if (this.checklist.RemindTime.Days === this.checklist.DueTime.Days) {
            maxValue = this.checklist.DueTime.Hours;
        } else if (this.checklist.RemindTime.Days < this.checklist.DueTime.Days) {
            const recurringHoursValues = this.dueTimeHoursDropdownSettings.dataSet.sort(propComparator('Id'));
            maxValue = recurringHoursValues[recurringHoursValues.length - 1].Id;
        }

        this.updateRemindTimeDropdown(this.remindTimeHoursDropdownSettings, maxValue, (id) => this.getHoursText(id));
    }

    timeSettingsChange() {
        if (this.checklist.RepeatFrequency === 0) this.checklist.RepeatFrequency = undefined;
        this.doPropagateChanges();
    }

    repeatFrequencyChange() {
        this.updateRecurringDays();
        this.resetRecurringMonths();
        this.resetRecurringDays();
        this.resetRecurringHours();
        this.resetDueTime();
        this.resetRemindTime();
        this.doPropagateChanges();
    }

    recurringMonthsChange() {
        this.updateRecurringDays();
        this.resetRecurringDays();
        this.resetDueTime();
        this.doPropagateChanges();
    }

    isRecurringMonthsRequired() {
        return this.isRequiredBasic();
    }

    recurringDaysChange() {
        this.updateDueTime();
        this.resetDueTime();
        this.doPropagateChanges();
    }

    isRecurringDaysRequired() {
        return this.isRequiredBasic();
    }

    recurringHoursChange() {
        this.updateDueTime();
        this.resetDueTime();
        this.doPropagateChanges();
    }

    isRecurringHoursRequired() {
        return this.isRequiredBasic();
    }

    private updateRemindTime() {
        this.updateRemindMonths();
        this.updateRemindDays();
        this.updateRemindHours();
        this.resetRemindTime();
        this.doPropagateChanges();
    }

    dueMonthsChange() {
        this.checklist.DueTime.Days = undefined;
        this.updateDueTime({ skipUpdateMonths: true });
        this.updateRemindTime();
    }

    dueDaysChange() {
        this.checklist.DueTime.Hours = undefined;
        if (this.isMonthly()) {
            this.updateRemindMonths();

            if (this.checklist.RemindTime.Months < this.remindTimeMonthsDropdownSettings.dataSet[0].Id) {
                this.resetRemindMonths();
            }

            if (this.checklist.DueTime.Months === this.checklist.RemindTime.Months) {
                this.updateRemindDays();
                this.resetRemindDays();
            }
        } else {
            this.updateDueTime({ skipUpdateMonths: true, skipUpdateDays: true });
            this.updateRemindTime();
        }
        this.doPropagateChanges();
    }

    dueHoursChange() {
        this.updateRemindDays();
        if (
            this.remindTimeDaysDropdownSettings.dataSet.length &&
            this.checklist.RemindTime.Days < this.remindTimeDaysDropdownSettings.dataSet[0].Id
        ) {
            this.resetRemindDays();
        }
        if (this.checklist.DueTime.Days === this.checklist.RemindTime.Days) {
            this.updateRemindHours();
            this.resetRemindHours();
        }
        this.doPropagateChanges();
    }

    remindTimeMonthsChange() {
        this.updateRemindDays();
        /**
         * do not reset days dropdown in "monthly" case when there are few selected items in "recurring days"
         * because in this case in "remind month" dropdown there is only one item selected and it is doesn't make
         * sense to reset days dropdown on selecting this value
         */
        if (!(this.isMonthly() && this.checklist.RecurringDays.length > 1)) this.resetRemindDays();

        this.doPropagateChanges();
    }

    remindTimeDaysChange() {
        this.updateRemindHours();
        this.resetRemindHours();
        this.doPropagateChanges();
    }

    remindTimeHoursChange() {
        this.doPropagateChanges();
    }

    isMonthly() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly;
    }

    isDaily() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Daily;
    }

    isShowMonthsDropdown() {
        return this.isMonthly();
    }

    isShowDaysDropdown() {
        return this.checklist.RepeatFrequency > 0;
    }

    isShowHoursDropdown() {
        return this.isDaily();
    }

    isDueTimeDisabled() {
        return (
            !this.checklist.RepeatFrequency ||
            this.checklist.RecurringMonths.length === 0 ||
            this.checklist.RecurringDays.length === 0 ||
            this.checklist.RecurringHours.length === 0 ||
            this.isEditTimeDisabled()
        );
    }

    isRemindTimeDisabled() {
        return (
            this.isDueTimeDisabled() ||
            !isDefined(this.checklist.DueTime.Days) ||
            (this.isDaily() && !isDefined(this.checklist.DueTime.Hours)) ||
            (this.isMonthly() && !isDefined(this.checklist.DueTime.Months))
        );
    }

    isRemindDaysDisabled() {
        return (
            this.isRemindTimeDisabled() ||
            (this.isMonthly() &&
                !isDefined(this.checklist.RemindTime.Months) &&
                this.checklist.RecurringDays.length < 2)
        );
    }

    isRemindHoursDisabled() {
        return (
            this.isRemindTimeDisabled() ||
            (this.isDaily() && !isDefined(this.checklist.RemindTime.Days) && this.checklist.RecurringHours.length < 2)
        );
    }

    getRecurringDaysTooltip() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly
            ? 'checklist.recurring_days_monthly_tooltip'
            : 'checklist.recurring_days_daily_tooltip';
    }

    getRemindTimeTooltip() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly
            ? 'checklist.remind_time_monthly_tooltip'
            : 'checklist.remind_time_daily_tooltip';
    }

    getDueTimeTooltip() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly
            ? 'checklist.due_time_monthly_tooltip'
            : 'checklist.due_time_daily_tooltip';
    }

    isRemindDueMonthRequired() {
        return this.isMonthly() && this.isRequiredBasic();
    }

    isRemindDueDaysRequired() {
        return this.isRequiredBasic();
    }

    isRemindDueHoursRequired() {
        return this.isDaily() && this.isRequiredBasic();
    }

    isShowRemindDueTime() {
        return this.checklist.RepeatFrequency > 0;
    }

    isShowSaveAsTemplate() {
        return this.isAdmin;
    }

    isCheckPresenceEnabledForCustomer() {
        return this.customerDataService.customerData.ChecklistPresenceEnabled;
    }

    getRemindTimeMaxDate() {
        if (this.checklist.PublicationSetting.PublicationType === PublicationType.Continous) return undefined;

        return this.remindTimeMaxDate;
    }

    isShowOneTimeRemindDate() {
        return (
            this.isOneTime(this.checklist) &&
            this.checklist.PublicationSetting.PublicationType === PublicationType.TimeLimited
        );
    }

    isPublicationSettingsDisabled() {
        if (this.checklist.IsTemplate || !this.checklist.Id) return false;

        return !this.checklist.HasEditAccess;
    }

    getPublicationSettingsTitle() {
        if (!this.isPublicationSettingsDisabled()) return '';

        return this.appTextService.get('checklist.share_settings_not_an_author');
    }

    isEditTimeDisabled() {
        if (this.checklist.IsTemplate || !this.checklist.Id) return false;

        return !(this.checklist.HasEditAccess && /*!this.checklist.IsPublished &&*/ !this.checklist.IsWithResponses);
    }

    getEditTimeDisabledTitle() {
        if (!this.isEditTimeDisabled()) return '';

        /*if (this.checklist.CreatedByUserId !== CcmSessionService.getUser().Id) {
            return this.appTextService.get('checklist.time_settings_not_an_author');
        }*/

        /*if (this.checklist.IsPublished && !this.checklist.IsWithResponses) {
            return this.appTextService.get('checklist.time_settings_unpublish');
        }

        if (this.checklist.IsPublished && this.checklist.IsWithResponses) {
            return this.appTextService.get('checklist.time_settings_unpublish_and_delete_responses');
        }*/

        if (/*!this.checklist.IsPublished && */ this.checklist.IsWithResponses)
            return this.appTextService.get('checklist.time_settings_delete_responses');
    }

    isShowReportsDisabled() {
        if (this.checklist.IsTemplate || !this.checklist.Id) return false;

        return !this.checklist.HasEditAccess;
    }

    getShowReportsDisabledTitle() {
        if (!this.isShowReportsDisabled()) return '';

        return this.appTextService.get('checklist.reports_only_author');
    }

    isRepeatFrequencyRequired() {
        return this.isRequiredBasic();
    }

    isRequiredBasic() {
        return !this.checklist.IsTemplate && this.checklist.PublicationSetting.IsShared;
    }

    getShareText() {
        let result = this.yesNoPipe.transform(this.checklist.PublicationSetting.IsShared);
        if (this.checklist.PublicationSetting.IsShared) {
            const textId =
                this.checklist.PublicationSetting.PublicationType === 1 ? 'common.continuous' : 'common.time_limited';
            result += ', ' + this.appTextService.get(textId).toLowerCase();
        }
        return result;
    }

    @ViewChild('timeSettingsRadioGroup', { static: false }) timeSettingsRadioGroup: MatRadioGroup;

    getSelectedTimeSettingsName() {
        return this.timeSettings.find((item) => item.id === this.checklist.TimeSetting).textId;
    }

    getIsQREnabled() {
        return this.checklist.CheckPresenceEnabled ? 'yes' : 'no';
    }

    expandContent() {
        this.collapsableContent.expandContent();
    }

    getDaysDropdownLabelTextId() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Monthly
            ? 'checklist.starting_days'
            : 'common.days';
    }

    getHoursDropdownLabelTextId() {
        return this.checklist.RepeatFrequency === ChecklistModel.RepeatFrequency.Daily
            ? 'checklist.starting_times'
            : 'common.hours';
    }
}

function numbersSort(a, b) {
    return a - b;
}

// month formula from here: https://habrahabr.ru/post/261773/
function getDaysInMonth(x) {
    return 28 + ((x + Math.floor(x / 8)) % 2) + (2 % x) + 2 * Math.floor(1 / x);
}
